/**
 * Voucherify Activity Mapper
 * 
 * Normalizes Voucherify activity events to a clean frontend contract.
 * Handles date parsing, event type extraction, label generation, and PII filtering.
 */

import { VoucherifyActivityItem } from '../types/voucherifyTypes'
import { PointsHistoryItemDTO } from '../types/pointsHistoryTypes'

/**
 * Event type to human-readable label mapping
 */
const EVENT_TYPE_LABELS: Record<string, string> = {
  login: 'Logged in',
  signup: 'Signed up',
  menu_scan_new: 'Scanned a new restaurant menu',
  menu_scan_existing: 'Scanned a menu',
  receipt_upload: 'Uploaded a receipt',
  referral_eater_signup: 'Referred an eater',
  referral_restaurant_signup: 'Referred a restaurant',
  'customer.voucher.loyalty_card.points_added': 'Points added',
  points_added: 'Points added',
  points_spent: 'Points spent',
  points_redeemed: 'Points redeemed',
}

/**
 * Generates a human-readable label from event type
 */
function generateLabel(eventType: string, metadata?: Record<string, any>): string {
  // Use mapped label if available
  if (EVENT_TYPE_LABELS[eventType]) {
    return EVENT_TYPE_LABELS[eventType]
  }
  
  // Never return "customer.custom_event" as label - use inner event type if available
  if (eventType === 'customer.custom_event' && metadata?.eventType) {
    return EVENT_TYPE_LABELS[metadata.eventType] || formatEventType(metadata.eventType)
  }
  
  // Format event type as fallback (title case, replace underscores)
  return formatEventType(eventType)
}

/**
 * Formats event type to readable string
 */
function formatEventType(eventType: string): string {
  return eventType
    .split(/[._-]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

/**
 * Extracts normalized event type from Voucherify item
 */
function extractEventType(item: VoucherifyActivityItem): string {
  // Prefer data.event.type if present
  if (item.data?.event?.type) {
    return item.data.event.type
  }
  
  // Fallback to outer type
  return item.type
}

/**
 * Extracts timestamp from Voucherify item (prefers data.event.created_at)
 */
function extractTimestamp(item: VoucherifyActivityItem): string {
  // Prefer data.event.created_at
  if (item.data?.event?.created_at) {
    return item.data.event.created_at
  }
  
  // Fallback to outer created_at
  if (item.created_at) {
    return item.created_at
  }
  
  // Last resort: current time (should not happen in practice)
  return new Date().toISOString()
}

/**
 * Extracts points delta from Voucherify item
 * Looks for points amounts in various locations
 */
function extractPointsDelta(item: VoucherifyActivityItem): number | null {
  // Check data.event.metadata for points
  if (item.data?.event?.metadata) {
    const metadata = item.data.event.metadata
    if (typeof metadata.points === 'number') {
      return metadata.points
    }
    if (typeof metadata.pointsDelta === 'number') {
      return metadata.pointsDelta
    }
    if (typeof metadata.points_added === 'number') {
      return metadata.points_added
    }
    if (typeof metadata.points_spent === 'number') {
      return -Math.abs(metadata.points_spent) // Negative for spent
    }
  }
  
  // Check outer metadata
  if (item.metadata) {
    if (typeof item.metadata.points === 'number') {
      return item.metadata.points
    }
    if (typeof item.metadata.pointsDelta === 'number') {
      return item.metadata.pointsDelta
    }
  }
  
  // Check data level
  if (item.data) {
    if (typeof item.data.points === 'number') {
      return item.data.points
    }
    if (typeof item.data.pointsDelta === 'number') {
      return item.data.pointsDelta
    }
    if (typeof item.data.amount === 'number' && item.data.event?.type?.includes('points')) {
      return item.data.amount
    }
  }
  
  // For loyalty card points_added events, check specific structure
  if (item.type === 'customer.voucher.loyalty_card.points_added' || 
      item.data?.event?.type === 'customer.voucher.loyalty_card.points_added') {
    if (typeof item.data?.balance === 'number') {
      return item.data.balance
    }
  }
  
  return null
}

/**
 * Extracts safe source information (no PII)
 */
function extractSource(item: VoucherifyActivityItem): string | undefined {
  const metadata = item.data?.event?.metadata ?? item.metadata
  
  if (metadata) {
    // Only return safe source identifiers (not email, name, etc.)
    if (typeof metadata.source === 'string' && 
        !metadata.source.includes('@') && 
        !metadata.source.includes('email')) {
      return metadata.source
    }
    if (typeof metadata.channel === 'string') {
      return metadata.channel
    }
    if (typeof metadata.source_id === 'string' && 
        !metadata.source_id.includes('@')) {
      return metadata.source_id
    }
  }
  
  return undefined
}

/**
 * Filters out PII from metadata
 */
function sanitizeMetadata(metadata?: Record<string, any>): Record<string, any> | undefined {
  if (!metadata) return undefined
  
  const piiFields = ['email', 'name', 'firstName', 'lastName', 'phone', 'address', 'customerId', 'appId', 'appToken']
  const sanitized: Record<string, any> = {}
  
  for (const [key, value] of Object.entries(metadata)) {
    // Skip PII fields
    if (piiFields.some(field => key.toLowerCase().includes(field.toLowerCase()))) {
      continue
    }
    
    // Skip if value contains email-like patterns
    if (typeof value === 'string' && value.includes('@')) {
      continue
    }
    
    sanitized[key] = value
  }
  
  return Object.keys(sanitized).length > 0 ? sanitized : undefined
}

/**
 * Maps a single Voucherify activity item to normalized DTO
 */
export function mapVoucherifyActivityItem(item: VoucherifyActivityItem): PointsHistoryItemDTO {
  const eventType = extractEventType(item)
  const timestamp = extractTimestamp(item)
  const pointsDelta = extractPointsDelta(item)
  const source = extractSource(item)
  const metadata = sanitizeMetadata(item.data?.event?.metadata ?? item.metadata)
  
  // Generate label
  const label = generateLabel(eventType, metadata)
  
  return {
    id: item.id,
    timestamp,
    eventType,
    label,
    pointsDelta: pointsDelta ?? null,
    rawType: item.type !== eventType ? item.type : undefined, // Only include if different from eventType
    source,
    category: item.data?.event?.metadata?.category || item.metadata?.category,
  }
}

/**
 * Maps Voucherify activity response to normalized response
 */
export function mapVoucherifyActivityResponse(
  response: any,
  sortNewestFirst: boolean = true
): { items: PointsHistoryItemDTO[]; nextPageToken?: string; hasMore: boolean } {
  // Extract items from various possible response structures
  const rawItems: VoucherifyActivityItem[] = 
    response?.data || 
    response?.items || 
    response?.activities || 
    []
  
  // Map to normalized DTOs
  let items = rawItems.map(mapVoucherifyActivityItem)
  
  // Sort newest first (by timestamp)
  if (sortNewestFirst) {
    items = items.sort((a, b) => {
      const dateA = new Date(a.timestamp).getTime()
      const dateB = new Date(b.timestamp).getTime()
      return dateB - dateA // Newest first
    })
  }
  
  return {
    items,
    nextPageToken: response?.nextPageToken,
    hasMore: response?.hasMore ?? false,
  }
}




